import { Sidebar } from './Sidebar';
import { useAuth } from '../../contexts/AuthContext';
import { UserCog, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { useState, useEffect, useCallback } from 'react';
import VoiceProfileAssessmentPopup from '../assessment/VoiceProfileAssessmentPopup';
import { StreakNotification } from '../gamification';
import ViolationBell from '../ViolationBell';
import { NewTaskPopup } from '../quality/NewTaskPopup';
import { EEOCNudgePopup } from '../eeoc/EEOCNudgePopup';
import { useQualitySummary } from '../../hooks/useQualitySummary';
import { isEEOCEligible, useEEOCCycle } from '../../hooks/useEEOCCycle';

// Session-level flag to track if we've already checked assessment status
const ASSESSMENT_CHECK_KEY = 'voice_assessment_checked';
const ASSESSMENT_DISMISSED_KEY = 'voice_assessment_dismissed';
const STREAK_NOTIFICATION_KEY = 'streak_notification_dismissed';

export const MainLayout = ({ children }) => {
    const { user, api, isTestUser, isAdmin } = useAuth();
    const qualityEligible = Boolean(user) && (isAdmin || user?.feature_access?.staffing_training !== false);
    const qualitySummary = useQualitySummary(qualityEligible);
    const eeocEligible = isEEOCEligible(user, isAdmin);
    const eeocCycle = useEEOCCycle(eeocEligible);
    const [resetting, setResetting] = useState(false);
    const [showAssessmentPopup, setShowAssessmentPopup] = useState(false);
    const [assessmentPending, setAssessmentPending] = useState(() => user?.role !== 'admin'
        && !sessionStorage.getItem(ASSESSMENT_CHECK_KEY) && !sessionStorage.getItem(ASSESSMENT_DISMISSED_KEY));
    const [streakData, setStreakData] = useState(null);
    const [showStreakNotification, setShowStreakNotification] = useState(false);
    
    // Check streak status for notification
    const checkStreakStatus = useCallback(async () => {
        if (!user || user.role === 'admin') return;
        
        const dismissed = sessionStorage.getItem(STREAK_NOTIFICATION_KEY);
        if (dismissed) return;
        
        try {
            const response = await api.get('/gamification/profile');
            const streak = response.data?.streak;
            
            if (streak && streak.current_streak > 0) {
                // Only show notification if user hasn't practiced today
                const lastActivity = streak.last_activity_date;
                const today = new Date().toDateString();
                const lastDate = lastActivity ? new Date(lastActivity).toDateString() : null;
                
                if (lastDate !== today) {
                    setStreakData(streak);
                    // Delay to not overwhelm user on page load
                    setTimeout(() => setShowStreakNotification(true), 5000);
                }
            }
        } catch (error) {
            // Silently fail
        }
    }, [user, api]);
    
    // Check if user needs voice profile assessment (not for admin)
    // Only check once per browser session using sessionStorage
    const checkAssessmentNeeded = useCallback(async () => {
        // Skip for admin users
        if (!user || user.role === 'admin') return;
        
        // Check if already checked this session
        const alreadyChecked = sessionStorage.getItem(ASSESSMENT_CHECK_KEY);
        const wasDismissed = sessionStorage.getItem(ASSESSMENT_DISMISSED_KEY);
        
        if (alreadyChecked || wasDismissed) return;
        
        try {
            const response = await api.get('/voice-profile-assessment/status');
            sessionStorage.setItem(ASSESSMENT_CHECK_KEY, 'true');
            
            if (response.data.needs_assessment) {
                // Delay showing the popup to not interrupt initial page load
                setTimeout(() => {
                    setShowAssessmentPopup(true);
                    setAssessmentPending(false);
                }, 3000);
            } else {
                setAssessmentPending(false);
            }
        } catch (error) {
            // Silently fail - don't block the app if assessment service is unavailable
            sessionStorage.setItem(ASSESSMENT_CHECK_KEY, 'true');
            setAssessmentPending(false);
        }
    }, [user, api]);
    
    useEffect(() => {
        checkAssessmentNeeded();
        checkStreakStatus();
    }, [checkAssessmentNeeded, checkStreakStatus]);
    
    const handleDismissStreak = () => {
        setShowStreakNotification(false);
        sessionStorage.setItem(STREAK_NOTIFICATION_KEY, 'true');
    };
    
    const handleDismissAssessment = () => {
        setShowAssessmentPopup(false);
        // Persist dismissal for this session
        sessionStorage.setItem(ASSESSMENT_DISMISSED_KEY, 'true');
    };
    
    const handleResetProgress = async () => {
        setResetting(true);
        try {
            await api.post('/users/test-user/reset-progress');
            toast.success('Progress reset!', { 
                description: 'All learning progress has been cleared. Refresh to see changes.' 
            });
            // Refresh the page to reflect changes
            window.location.reload();
        } catch (error) {
            toast.error('Failed to reset progress');
        } finally {
            setResetting(false);
        }
    };
    
    return (
        <div className="min-h-screen bg-background">
            <Sidebar qualitySummary={qualitySummary} />
            <main className="pl-64">
                {/* Top Bar with Violation Bell - Always visible for logged-in users */}
                <div className="sticky top-0 z-40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b">
                    <div className="flex items-center justify-end px-6 py-2">
                        <ViolationBell />
                    </div>
                </div>
                
                {/* Test Mode Banner */}
                {(user?.is_test_user || user?.role === 'trainee') && (
                    <div className="sticky top-[52px] z-50 bg-gradient-to-r from-amber-500 to-orange-500 text-white px-6 py-2">
                        <div className="flex items-center justify-between max-w-7xl mx-auto">
                            <div className="flex items-center gap-3">
                                <UserCog className="h-5 w-5" />
                                <span className="font-medium">Test Mode Active</span>
                                <span className="text-amber-100 text-sm">
                                    You are logged in as Admin Trainee for testing purposes
                                </span>
                            </div>
                            <div className="flex items-center gap-2">
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={handleResetProgress}
                                    disabled={resetting}
                                    className="text-white hover:bg-white/20 gap-2"
                                >
                                    <RefreshCw className={`h-4 w-4 ${resetting ? 'animate-spin' : ''}`} />
                                    Reset Progress
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
                <div className="min-h-screen p-6 md:p-8 lg:p-10">
                    {children}
                </div>
            </main>
            
            {/* Voice Profile Assessment Popup - shows for non-admin users */}
            {showAssessmentPopup && user?.role !== 'admin' && (
                <VoiceProfileAssessmentPopup 
                    onComplete={handleDismissAssessment}
                    onDismiss={handleDismissAssessment}
                />
            )}
            
            {/* Streak Notification - reminds users to maintain their streak */}
            {showStreakNotification && streakData && (
                <StreakNotification 
                    streak={streakData.current_streak}
                    lastActivityDate={streakData.last_activity_date}
                    onDismiss={handleDismissStreak}
                />
            )}

            <div className="pointer-events-none fixed bottom-4 right-4 z-50 flex max-h-[calc(100vh-2rem)] w-[calc(100vw-2rem)] max-w-sm flex-col gap-3 overflow-y-auto sm:bottom-6 sm:right-6 sm:max-h-[calc(100vh-3rem)]" data-testid="popup-stack">
                {eeocEligible && <EEOCNudgePopup cycle={eeocCycle} blocked={assessmentPending || showAssessmentPopup} />}
                {qualityEligible && <NewTaskPopup summary={qualitySummary} blocked={assessmentPending || showAssessmentPopup} />}
            </div>

        </div>
    );
};
